<script setup>
import { gsap } from '~/vendors/gsap'
import SplitType from 'split-type'
import { useIsMobile } from '~~/composables/useIsMobile'

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
})

const route = useRoute()
const isHome = computed(() => route.name.startsWith('index'))
const isMobile = useIsMobile()

/**
 * Link rendering
 */
const localePath = useLocalePath()
const link = computed(() => props.item.link)
const isInternal = computed(() => !!link.value.page)
const isDownload = computed(() => !!link.value.download)
const href = computed(() => {
  if (isInternal.value) {
    return localePath(`/${link.value.page?.slug}`)
  }

  if (isDownload.value) {
    return link.value.download?.url
  }

  return link.value.url
})

/**
 * Hover animation
 */
const isHovering = ref(false)
const el = ref(null)
let timeline
let timeline2

function onMouseenter() {
  if (isMobile.value) return
  const chevron = el.value.querySelector('.button-icon__icon')
  const eyebrow = el.value.querySelector('.footer-link__eyebrow')
  const container = el.value.querySelector(
    '.footer-link__description-container'
  )
  const content = el.value.querySelector('.footer-link__description')
  isHovering.value = true

  if (container) {
    const st = new SplitType(content, {
      types: 'lines',
    })

    const { top: startTop } = eyebrow.getBoundingClientRect()
    container.style.height = 'auto'
    container.style.overflow = 'visible'
    const { top: endTop } = eyebrow.getBoundingClientRect()

    if (timeline) {
      timeline.seek(timeline.duration())
      timeline.kill()
    }

    timeline = gsap
      .timeline()
      .from(
        eyebrow,
        {
          y: startTop - endTop,
          duration: st.lines.length * 0.5,
          ease: 'power2.out',
        },
        0
      )

      .from(
        st.lines,
        {
          opacity: 0,
          y: 15,
          duration: st.lines.length * 0.5,
          stagger: 0.15,
          ease: 'power2.out',
          lazy: false,
          onComplete() {
            // st.revert()
          },
        },
        0
      )

    timeline2 = gsap
      .timeline()
      .to(chevron, {
        scaleX: 1.1,
        scaleY: 0.5,
        x: 15,
        opacity: 0,
        duration: 0.3,
        ease: 'power3.in',
      })
      .to(chevron, {
        x: -15,
        duration: 0,
      })
      .to(chevron, {
        scaleX: 1,
        scaleY: 1,
        x: 0,
        opacity: 1,
        duration: 0.3,
        ease: 'power3.out',
      })
  }
}

function onMouseleave() {
  if (isMobile.value) return
  const eyebrow = el.value.querySelector('.footer-link__eyebrow')
  const container = el.value.querySelector(
    '.footer-link__description-container'
  )
  const content = el.value.querySelector('.footer-link__description')
  isHovering.value = false

  if (container) {
    const st = new SplitType(content, {
      types: 'lines',
    })

    const { top: startTop } = eyebrow.getBoundingClientRect()
    container.style.height = null
    const { top: endTop } = eyebrow.getBoundingClientRect()

    if (timeline) {
      timeline.seek(timeline.duration())
      timeline.kill()
    }
    timeline = gsap
      .timeline()
      .from(
        eyebrow,
        {
          y: startTop - endTop,
          duration: st.lines.length * 0.5,
          ease: 'power2.out',
        },
        0
      )
      .fromTo(
        st.lines,
        {
          opacity: 1,
          y: startTop - endTop,
        },
        {
          opacity: 0,
          y: startTop - endTop + 15,
          duration: st.lines.length * 0.5,
          stagger: 0.15,
          ease: 'power2.out',
          lazy: false,
          onComplete() {
            container.style.overflow = null
            st.revert()
          },
        },
        0
      )
  }
}
</script>

<template>
  <div v-if="link" class="footer-link" ref="el">
    <article
      class="footer-link__item"
      :style="{
        backgroundImage: item.backgroundImage
          ? `url(${item.backgroundImage.url})`
          : '',
      }"
    >
      <div class="footer-link__texts">
        <p class="footer-link__eyebrow text__quote">{{ item.title }}</p>
        <div v-if="item.copy" class="footer-link__description-container">
          <p class="footer-link__description">
            {{ item.copy }}
          </p>
        </div>

        <slot />

        <CallToAction
          v-if="isHome"
          v-bind="item.link"
          class="footer-link__cta"
          :gtmClass="item.linkGtmClass"
          outline
          icon="chevron-right"
          animate
        />
      </div>

      <template v-if="!isHome">
        <ButtonIcon
          class="footer-link__button"
          tag="div"
          icon="chevron-right"
          show-highlight
          :rotate-highlight="isHovering"
        />

        <NuxtLink
          class="footer-link__link"
          :class="item.linkGtmClass"
          @mouseenter="onMouseenter"
          @mouseleave="onMouseleave"
          :to="href"
        />
      </template>
    </article>
  </div>
</template>

<style lang="scss">
// Vendors
@import 'styles/vendors/sass-mq/mq';

// Utils
@import 'styles/utils/variables';
@import 'styles/utils/functions';

.footer-link {
  &__item {
    background-color: #000;
    background: url('/assets/images/homepage/footer-mobile.png');
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
    padding: 0 var(--grid-edge) var(--grid-edge);
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
    height: 516px;
    position: relative;

    @include mq($from: 'l') using($from) {
      height: 100%;
      background-image: url('/assets/images/homepage/footer-desktop.png');
      padding-bottom: 56px;
    }

    &::before {
      content: '';

      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      background: radial-gradient(
          117.57% 117.57% at 50% 50%,
          rgba(17, 9, 32, 0) 0%,
          rgba(17, 9, 32, 0.3) 100%
        ),
        linear-gradient(32.03deg, #0e061e -6.21%, rgba(14, 5, 31, 0) 75.06%);
      mix-blend-mode: normal;
    }
  }

  &__texts {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-start;
    width: gs(4, 4, 'xxxs');
    position: relative;

    @include mq($from: 'l') using($from) {
      margin-top: 232px;
      width: gs(8, 7, $from);
      max-width: 426px;
    }
  }

  &__eyebrow {
    font-weight: $font-weight-3G;
  }

  &__cta {
    margin-top: 46px;

    @include mq($until: 'l') using($until) {
      margin-top: 30px;
    }

    &.button-pill {
      margin-top: 0;
    }
  }

  &__description-container {
    @include mq($from: 'l') using ($from) {
      overflow: hidden;
      height: 0;
      .page-home__footer-links & {
        overflow: visible;
        height: auto;
      }
    }
  }

  &__description {
    margin-top: 20px;
  }

  &__button .icon {
    color: $brand-white;
    width: 14px;
    height: 14px;
    transition: none;
  }

  &__link {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }

  .footer-links--multi & {
    .footer-link__texts {
      @include mq($from: 'l') using($from) {
        width: gs(5, 4, $from);
      }
    }
  }
}
</style>
